import { useEffect, useState } from 'react';
import {useParams} from "react-router-dom"
import dotProp from "dot-prop-immutable"
import _ from 'lodash';

import { useFirebase } from '@meddbriefer/mdb-firebase';
import { getScenarioByID } from "@meddbriefer/scenario-data"

import { useScenarioEditor } from './ScenarioEditorContext';

import ScenarioEditorLayout from './ScenarioEditorLayout';

import { validateScenarioNameUniqueness, ignoreTrailingSpaceInStrings } from './formUtils';

const pt = 'patientInformation';

export default function BasicInfo() {
	const {db} = useFirebase()
	const {scenarioId} = useParams()
	const {scenario, updateScenarioFld, saveScenario } = useScenarioEditor()
	const [state, setState] = useState(null)
	const [origState, setOrigState] = useState(null)
	const [stateUpdated, setStateUpdated] = useState(false)
	const [hasChanges, setHasChanges] = useState(false)
	const [readyToSave, setReadyToSave] = useState(false)

	const performValidation = () => {
		return validateScenarioNameUniqueness(db, state.name, scenario)
	}
	const saveHandler = async (event) => {
		if (! await performValidation()) {
			alert("scenario name is not unique")
		} else {
			setReadyToSave(true)
		}
	}

	const updateState = (fldPath, value) => {
		setState(dotProp.set(state, fldPath, value))
		setStateUpdated(true)
	}

	const blurNameHandler = async (event) => {
		if (! await validateScenarioNameUniqueness(db, state.name, scenario)) {
			alert("scenario name is not unique")
		}
	}

	const radioChange = (event) => {
		const {name, value} = event.target
		updateState(name, value === "true")
	}

	const changeHandler = (event) => {
		const {id: fldPath, value} = event.target
		updateState(fldPath, value)
	}

	const changeNumericFieldHandler = (event) => {
		const {id: fldPath, value} = event.target
		let val = parseInt(value, 10)
		updateState(fldPath, val)
	}

	useEffect(
		() => {
			if (!state) {
				// *should* only run on initial render
				const tmp = {
					name: scenario.name,
					info: scenario.info
				}
				setState(tmp)
				setOrigState(tmp)
			}
		}, [scenarioId, scenario, state]
	)

	useEffect(
		() => {
			if (stateUpdated) {
				// prevent endless loop
				setStateUpdated(false)
				setHasChanges(! _.isEqualWith(state, origState, ignoreTrailingSpaceInStrings))
			}
		}, [stateUpdated, state, origState]
	)

	useEffect(
		() => {
			if (readyToSave) {
				setReadyToSave(false)
				// persist local state to scenario
				updateScenarioFld("name", state.name)
				updateScenarioFld("info", state.info)
				// set form to no longer have changes
				setOrigState(state)
				setHasChanges(false)
				//persist local state to db
				const saveToDB = async () => {
					// NOTE: only update the db with the data from our local state
					const scenData = {
						...await getScenarioByID(db, scenarioId),
						name: state.name,
						info: state.info
					}
					await saveScenario(scenData)
				}
				saveToDB()
			}
		}, [db, readyToSave, scenarioId, state, updateScenarioFld, saveScenario]
	)


	if (!state) {
		return (<div>loading...</div>)
	}
	return (
		<ScenarioEditorLayout
			label="Save changes to Info"
			haveChanges={hasChanges}
			saveHandler={saveHandler}
		>
			<div id="basic-info" className="info-wrapper">
				<h1>Basic Information:</h1>
				<label>
					Scenario Name:
					<input
						id="name"
						type="text"
						value={dotProp.get(state, 'name')}
						onChange={changeHandler}
						onBlur={blurNameHandler}

					/>
				</label>
				<div>
					Followup with Debriefing:
					<label style={{display: "inline-flex"}}>
						<input
							style={{display: "inline-flex"}}
							type="radio"
							name="info.debriefing"
							value={true}
							checked={dotProp.get(state, 'info.debriefing') === true}
							onChange={radioChange}
						/>
						Yes
					</label>
					<label style={{display: "inline-flex"}}>
						<input
							style={{display: "inline-flex"}}
							type="radio"
							name="info.debriefing"
							value={false}
							checked={dotProp.get(state, 'info.debriefing') === false}
							onChange={radioChange}
						/>
						No
					</label>
				</div>
				<label>
					Instructor Info:
					<textarea
						id="info.instructorInformation"
						value={dotProp.get(state, 'info.instructorInformation')}
						onChange={changeHandler}
					/>
				</label>
				<label>
					Dispatch Info:
					<textarea
						id="info.dispatchInfo"
						value={dotProp.get(state, 'info.dispatchInfo')}
						onChange={changeHandler}
					/>
				</label>
				<label>
					Scene assessment:
					<textarea
						id="info.sceneAssessment"
						value={dotProp.get(state, 'info.sceneAssessment')}
						onChange={changeHandler}
					/>
				</label>
				<label>
					Maximum time for scenario:
					<input
						id="info.maxTime"
						type="number"
						placeholder="minutes"
						value={dotProp.get(state, 'info.maxTime')}
						onChange={changeNumericFieldHandler}
					/>
				</label>
				<h1>Patient Info:</h1>
				<div id="pt-info">
					<label>
						Moulage:
						<input
							type="text"
							id={`info.${pt}.moulage`}
							value={dotProp.get(state, 'info.patientInformation.moulage')}
							onChange={changeHandler}
						/>
					</label>
					<label>
						Position:
						<input
							type="text"
							id={`info.${pt}.position`}
							value={dotProp.get(state, 'info.patientInformation.position')}
							onChange={changeHandler}
						/>
					</label>
					<label>
						Actions:
						<input
							type="text"
							id={`info.${pt}.actions`}
							value={dotProp.get(state, 'info.patientInformation.actions')}
							onChange={changeHandler}
						/>
					</label>
					<label>
						Age:
							<input
								type="number"
								id={`info.${pt}.age`}
								value={dotProp.get(state, 'info.patientInformation.age')}
								onChange={changeNumericFieldHandler}
							/>
					</label>
					<label>
						Weight:
							<input
								type="text"
								id={`info.${pt}.weight`}
								value={dotProp.get(state, 'info.patientInformation.weight')}
								onChange={changeHandler}
							/>
					</label>
					<label>
						Gender:
							<input
								type="text"
								id={`info.${pt}.gender`}
								value={dotProp.get(state, 'info.patientInformation.gender')}
								onChange={changeHandler}
							/>
					</label>
				</div>
			</div>
		</ScenarioEditorLayout>
	);
}

